import React, { useCallback, useEffect, useState } from "react";
import KlubbVelger, { getKlubbId, getKlubbNavn } from "./KlubbVelger";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function splitList(value) {
  return value.split(",").map((s) => s.trim()).filter(Boolean);
}

function ImportFromMasterModal({ onClose, onImported }) {
  const [master, setMaster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API_BASE_URL}/klubbsystem/products/master-catalog?clubId=${getKlubbId()}`)
      .then((res) => {
        if (!res.ok) throw new Error("Kunne ikke hente masterkatalog");
        return res.json();
      })
      .then(setMaster)
      .catch((err) => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const brands = [...new Set(master.map((m) => m.brand).filter(Boolean))].sort((a, b) => a.localeCompare(b));

  const filtered = master.filter((m) => {
    if (brandFilter && m.brand !== brandFilter) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      m.productName.toLowerCase().includes(q) ||
      (m.ean || "").toLowerCase().includes(q) ||
      (m.brand || "").toLowerCase().includes(q) ||
      (m.sku || "").toLowerCase().includes(q)
    );
  });

  const toggleRow = (ean) => setSelected((s) => (s[ean] ? { ...s, [ean]: undefined } : { ...s, [ean]: true }));
  const selectedEans = Object.keys(selected).filter((ean) => selected[ean]);

  // "Velg alle" gjelder de synlige (søkefiltrerte) radene, ikke nødvendigvis hele
  // masterkatalogen — matcher det brukeren faktisk ser. Bevarer valg utenfor filteret.
  const allFilteredSelected = filtered.length > 0 && filtered.every((m) => selected[m.ean]);
  const toggleAllFiltered = () => {
    setSelected((s) => {
      const next = { ...s };
      for (const m of filtered) next[m.ean] = !allFilteredSelected;
      return next;
    });
  };

  const submit = async () => {
    setError("");
    if (selectedEans.length === 0) return setError("Velg minst én vare");
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/klubbsystem/products/import-from-master`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clubId: getKlubbId(), eans: selectedEans }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Kunne ikke importere");
      }
      await onImported();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto p-6">
        <div className="flex items-start justify-between mb-2">
          <h2 className="text-lg font-semibold text-gray-900">Importer fra masterkatalog</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Masterkatalogen er felles for alle klubber — varene du velger legges kun til <strong>{getKlubbNavn()}</strong>s vareliste.
        </p>

        {loading && <p className="text-gray-400 text-sm">Laster …</p>}
        {loadError && <p className="text-red-600 text-sm">{loadError}</p>}

        {!loading && !loadError && (
          master.length === 0 ? (
            <p className="text-gray-400 text-sm">Masterkatalogen er tom. Last opp Sport 1s varekatalog under Fakturaimport først.</p>
          ) : (
            <>
              <div className="flex gap-2 mb-3">
                <input
                  placeholder="Søk på produktnavn, EAN eller merke"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <select
                  value={brandFilter}
                  onChange={(e) => setBrandFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Alle merker</option>
                  {brands.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>
              <div className="border border-gray-200 rounded-xl overflow-hidden mb-3 max-h-96 overflow-y-auto">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide sticky top-0">
                      <tr>
                        <th className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={allFilteredSelected}
                            onChange={toggleAllFiltered}
                            title="Velg alle"
                          />
                        </th>
                        <th className="text-left px-3 py-2">Produkt</th>
                        <th className="text-left px-3 py-2">Varenummer</th>
                        <th className="text-left px-3 py-2">Størrelse</th>
                        <th className="text-left px-3 py-2">Farge</th>
                        <th className="text-left px-3 py-2">Merke</th>
                        <th className="text-left px-3 py-2">EAN</th>
                        <th className="text-right px-3 py-2">Veilpris</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((m) => (
                        <tr key={m.ean} className="border-t border-gray-100">
                          <td className="px-3 py-2">
                            <input type="checkbox" checked={!!selected[m.ean]} onChange={() => toggleRow(m.ean)} />
                          </td>
                          <td className="px-3 py-2 text-gray-900">{m.productName}</td>
                          <td className="px-3 py-2 text-gray-500">{m.sku || "—"}</td>
                          <td className="px-3 py-2 text-gray-500">{m.size || "—"}</td>
                          <td className="px-3 py-2 text-gray-500">{m.color || "—"}</td>
                          <td className="px-3 py-2 text-gray-500">{m.brand || "—"}</td>
                          <td className="px-3 py-2 text-gray-500">{m.ean}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{m.recommendedPrice ? `${m.recommendedPrice} kr` : "—"}</td>
                          <td className="px-3 py-2">
                            {m.inCatalog && <span className="text-xs text-gray-400">Allerede i vareliste</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {error && <p className="text-red-600 text-sm mb-2">{error}</p>}

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  {selectedEans.length} vare{selectedEans.length === 1 ? "" : "r"} valgt
                  {" · "}
                  <button type="button" onClick={toggleAllFiltered} className="text-indigo-600 hover:text-indigo-500 font-medium">
                    {allFilteredSelected ? "Fjern alle" : "Velg alle"}
                  </button>
                </span>
                <button
                  onClick={submit}
                  disabled={busy || selectedEans.length === 0}
                  className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold disabled:opacity-50"
                >
                  {busy ? "Importerer …" : "Importer valgte varer"}
                </button>
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
}

export default function Produkter() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({ name: "", ean: "", sku: "", clubPrice: "", sizes: "", colors: "", printOptions: "" });

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/klubbsystem/products?clubId=${getKlubbId()}&includeInactive=true`);
      if (!res.ok) throw new Error("Kunne ikke hente vareliste");
      const data = await res.json();
      data.sort((a, b) => a.name.localeCompare(b.name));
      setProducts(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function createProduct(e) {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/klubbsystem/products`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clubId: getKlubbId(),
          name: form.name.trim(),
          ean: form.ean.trim(),
          sku: form.sku,
          clubPrice: Number(form.clubPrice) || 0,
          sizes: splitList(form.sizes),
          colors: splitList(form.colors),
          printOptions: splitList(form.printOptions),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Kunne ikke opprette produkt");
      }
      setForm({ name: "", ean: "", sku: "", clubPrice: "", sizes: "", colors: "", printOptions: "" });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  function startEdit(product) {
    setEditingId(product._id);
    setEditForm({
      name: product.name,
      ean: product.ean || "",
      sku: product.sku || "",
      clubPrice: product.clubPrice ? String(product.clubPrice) : "",
      sizes: (product.sizes || []).join(", "),
      colors: (product.colors || []).join(", "),
    });
    setError("");
  }

  function cancelEdit() {
    setEditingId(null);
    setEditForm(null);
  }

  async function saveEdit(product) {
    if (!editForm.name.trim()) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/klubbsystem/products/${product._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: editForm.name.trim(),
          ean: editForm.ean.trim(),
          sku: editForm.sku.trim(),
          clubPrice: Number(editForm.clubPrice) || 0,
          sizes: splitList(editForm.sizes),
          colors: splitList(editForm.colors),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Kunne ikke oppdatere produkt");
      }
      setEditingId(null);
      setEditForm(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(product) {
    setBusy(true);
    try {
      await fetch(`${API_BASE_URL}/klubbsystem/products/${product._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active: !product.active }),
      });
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function deleteProduct(product) {
    if (!window.confirm(`Slette «${product.name}» permanent? Dette kan ikke angres.`)) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/klubbsystem/products/${product._id}`, { method: "DELETE" });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Kunne ikke slette produkt");
      }
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-4xl mx-auto py-10 px-4">
      <span className="inline-block px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold mb-3">
        Klubb
      </span>
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">Vareliste</h1>
      <p className="text-gray-500 mb-6">
        Klubbens produktkatalog forvaltes herfra. Klubben bruker den skrivebeskyttet, bl.a. for å
        velge varer når de registrerer lagerbeholdning.
      </p>

      <KlubbVelger onChange={load} />

      <div className="flex justify-end mb-3">
        <button
          onClick={() => setShowImport(true)}
          className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-semibold"
        >
          Importer fra masterkatalog
        </button>
      </div>

      {loading && <p className="text-gray-400 text-sm">Laster …</p>}
      {error && <p className="text-red-600 text-sm mb-3">{error}</p>}

      {!loading && (
        products.length === 0 ? (
          <p className="text-gray-400 text-sm mb-6">Ingen produkter i vareliste ennå.</p>
        ) : (
          <div className="border border-gray-200 rounded-xl overflow-hidden mb-8">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-2.5">Produkt</th>
                    <th className="text-left px-4 py-2.5">EAN</th>
                    <th className="text-left px-4 py-2.5">Varenr.</th>
                    <th className="text-right px-4 py-2.5">Pris</th>
                    <th className="text-left px-4 py-2.5">Størrelser</th>
                    <th className="text-left px-4 py-2.5">Farger</th>
                    <th className="text-left px-4 py-2.5">Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => {
                    const editing = editingId === p._id;
                    const editInputCls = "w-full border border-gray-300 rounded-md px-2 py-1 text-sm";
                    if (editing) {
                      return (
                        <tr key={p._id} className="border-t border-gray-100 bg-indigo-50/40">
                          <td className="px-4 py-2.5">
                            <input className={editInputCls} value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} required />
                          </td>
                          <td className="px-4 py-2.5">
                            <input className={editInputCls} value={editForm.ean} onChange={(e) => setEditForm((f) => ({ ...f, ean: e.target.value }))} />
                          </td>
                          <td className="px-4 py-2.5">
                            <input className={editInputCls} value={editForm.sku} onChange={(e) => setEditForm((f) => ({ ...f, sku: e.target.value }))} />
                          </td>
                          <td className="px-4 py-2.5">
                            <input className={`${editInputCls} text-right`} type="number" value={editForm.clubPrice} onChange={(e) => setEditForm((f) => ({ ...f, clubPrice: e.target.value }))} />
                          </td>
                          <td className="px-4 py-2.5">
                            <input className={editInputCls} placeholder="kommasep." value={editForm.sizes} onChange={(e) => setEditForm((f) => ({ ...f, sizes: e.target.value }))} />
                          </td>
                          <td className="px-4 py-2.5">
                            <input className={editInputCls} placeholder="kommasep." value={editForm.colors} onChange={(e) => setEditForm((f) => ({ ...f, colors: e.target.value }))} />
                          </td>
                          <td className="px-4 py-2.5">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${p.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                              {p.active ? "Aktiv" : "Deaktivert"}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right whitespace-nowrap">
                            <button disabled={busy} onClick={() => saveEdit(p)} className="text-indigo-600 hover:text-indigo-500 text-xs font-semibold mr-3">
                              Lagre
                            </button>
                            <button disabled={busy} onClick={cancelEdit} className="text-gray-400 hover:text-gray-600 text-xs font-medium">
                              Avbryt
                            </button>
                          </td>
                        </tr>
                      );
                    }
                    return (
                    <tr key={p._id} className={`border-t border-gray-100 ${p.active ? "" : "opacity-50"}`}>
                      <td className="px-4 py-2.5 font-medium text-gray-900">{p.name}</td>
                      <td className="px-4 py-2.5 text-gray-500">{p.ean || "—"}</td>
                      <td className="px-4 py-2.5 text-gray-500">{p.sku || "—"}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{p.clubPrice ? `${p.clubPrice} kr` : "—"}</td>
                      <td className="px-4 py-2.5 text-gray-500">{p.sizes?.join(", ") || "—"}</td>
                      <td className="px-4 py-2.5 text-gray-500">{p.colors?.join(", ") || "—"}</td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${p.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                          {p.active ? "Aktiv" : "Deaktivert"}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <button disabled={busy} onClick={() => startEdit(p)} className="text-indigo-600 hover:text-indigo-500 text-xs font-medium mr-3">
                          Endre
                        </button>
                        <button disabled={busy} onClick={() => toggleActive(p)} className="text-indigo-600 hover:text-indigo-500 text-xs font-medium mr-3">
                          {p.active ? "Deaktiver" : "Aktiver"}
                        </button>
                        <button disabled={busy} onClick={() => deleteProduct(p)} className="text-gray-400 hover:text-red-600 text-xs font-medium">
                          Slett
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      <div className="border border-gray-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Nytt produkt</h2>
        <form onSubmit={createProduct} className="flex flex-wrap gap-2">
          <input className="flex-1 min-w-[160px] border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Produktnavn" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
          <input className="w-36 border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="EAN (valgfritt)" value={form.ean} onChange={(e) => setForm((f) => ({ ...f, ean: e.target.value }))} />
          <input className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Varenummer" value={form.sku} onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))} />
          <input className="w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Pris" type="number" value={form.clubPrice} onChange={(e) => setForm((f) => ({ ...f, clubPrice: e.target.value }))} />
          <input className="flex-1 min-w-[160px] border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Størrelser (kommasep.)" value={form.sizes} onChange={(e) => setForm((f) => ({ ...f, sizes: e.target.value }))} />
          <input className="flex-1 min-w-[160px] border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Farger (kommasep.)" value={form.colors} onChange={(e) => setForm((f) => ({ ...f, colors: e.target.value }))} />
          <input className="flex-1 min-w-[200px] border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Trykkalternativer (kommasep.)" value={form.printOptions} onChange={(e) => setForm((f) => ({ ...f, printOptions: e.target.value }))} />
          <button type="submit" disabled={busy} className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold disabled:opacity-50">
            {busy ? "Lagrer …" : "+ Nytt produkt"}
          </button>
        </form>
      </div>

      {showImport && <ImportFromMasterModal onClose={() => setShowImport(false)} onImported={load} />}
    </div>
  );
}
